import {logger} from './logger';
import axios from 'axios';
import store from './checkout/s';
import {runInAction} from 'mobx';
import {Portal, getAntiForgeryToken, modal} from './util';
import {ICheckoutType, ga4_item, ICartPricing, ICheckoutStep, IPlacePredictions, ICheckoutObject, old_gaProduct, ga4_dle_begin_checkout, ga4_dle_view_cart, ga4_dle_add_shipping_info, ga4_dle_add_payment_info} from './checkout/i';
import {cartLineToGa4Item, clearEcommerce, pushPendingAddToCart} from './checkout/ga4-cart';

declare var ga: any;
declare var dataLayer: any[];
declare var klaviyo: any[];
declare function marketing_atc(_arr: ga4_item[]);
declare function marketing_atc_pint(_arr: ga4_item[], em: string);
declare function marketing_purchase(_arr: ga4_item[]);
declare var __company_name: string;
declare var __domain_name: string;

const marketingEventsRun = new Set<string>();

function runMarketingOnce(key: string, callback: () => void): void {
    if (marketingEventsRun.has(key)) {
        return;
    }

    const storageKey = `checkout:marketing:v1:${key}`;
    try {
        if (window.sessionStorage.getItem(storageKey) === '1') {
            marketingEventsRun.add(key);
            return;
        }
    } catch {
        // Storage may be blocked; the in-memory set still prevents repeats on this page.
    }

    // Record the attempt before invoking scripts: a script may send an event and then throw.
    marketingEventsRun.add(key);
    try {
        window.sessionStorage.setItem(storageKey, '1');
    } catch {
        // Keep the in-memory flag when storage is unavailable or full.
    }

    try {
        callback();
    } catch (err) {
        // One marketing script must not interrupt checkout or the remaining events.
        logger.error(err);
    }
}

export const CHECKOUT_REQUEST_FAILED_MODAL_ID = 'modal-checkout-request-failed';

/**
 * POST helper. A failed request rejects (axios rejects every non-2xx status and every network failure), so it can never be
 * mistaken for a response body. Not reported here: `checkout()`, the only caller, reports it once.
 */
const req = (_url: string, o: IAPIContainer): Promise<IAPIContainer> => {
    // console.log('req', o);
    // console.log(JSON.stringify(o).length, o);

    const config = {
        headers: {
            RequestVerificationToken: getAntiForgeryToken(),
        },
    };

    const x = 1;
    return axios.post(_url, o, config).then(response => {
        // console.log('resp', response.data);
        return response.data as IAPIContainer;
    });
};

// TODO FIX MARKETING
export const checkout = async (k: string): Promise<boolean> => {
    const _url: string = '/api/checkout';
    runInAction(() => {
        // approved ria
        checkout_set_gift_messages();
    });

    const isError: boolean = k === 'error-log' ? true : false;

    // console.log(k);
    const o: IAPIContainer = {
        route: isError ? 'error' : 'checkout',
        key: k,
        auth: '',
        id: 0,
        msg: '',
        payload: JSON.stringify(store.checkout),
        ip: '',
        numbers_int: [],
        numbers_dec: [],
        booleans: [],
        debug: [],
    };

    if (k === 'cc-place-order') {
        runInAction(() => {
            // approved ria
            store.checkout.billing.ccNumber.value = '';
            store.checkout.billing.ccExp.value = '';
            store.checkout.billing.ccCSC.value = '';
        });
    }

    if (isError) {
        o.payload = encodeURI(store.errorLogInfo);
        o.msg = encodeURI(store.errorLog);
    }

    const _oldStep = store.checkout.cstep;
    // console.log('old ' + k, _oldStep);

    return req(_url, o)
        .then(x => {
            if (isError) {
                return true;
            }
            if (x.debug != undefined && x.debug.length > 0) {
                for (let i = 0; i < x.debug.length; i++) {
                    // console.log(JSON.parse(x.debug[i]));
                }
            }

            if (x.key === 'places-autocomplete') {
                runInAction(() => {
                    // approved ria
                    store.checkout.ship.google_autocomplete = JSON.parse(x.payload) as IPlacePredictions[];
                });
                return true;
            } else if (x.key === 'get-est') {
                var _d = JSON.parse(x.payload) as ICartPricing;
                if ((!store.checkout.cart.pricing.shipping || store.checkout.cart.pricing.shipping <= 0) && _d && _d.isEstShipping) {
                    runInAction(() => {
                        // approved ria
                        store.checkout.cart.pricing.isEstShipping = true;
                        store.checkout.cart.pricing.isEstTaxes = true;
                        store.checkout.cart.pricing.shipping = _d.shipping;
                        store.checkout.cart.pricing.tax = _d.tax;
                        // console.log('set est');
                    });
                }
                // console.log('est', _d);
                return true;
            } else if (x.key === 'addr-ship-address-book-id' || x.key === 'addr-bill-address-book-id') {
                return true;
            } else if (x.key === 'token-reset') {
                return true;
            }

            const _latest: ICheckoutObject = JSON.parse(x.payload) as ICheckoutObject;
            // console.log('new from server via key ' + k, _latest.CheckoutType);

            // only sync prices with servers if the last request was a sync from client to server
            runInAction(() => {
                // approved ria
                if (o.key.includes('sync')) {
                    store.checkout.cart.pricing = JSON.parse(JSON.stringify(_latest.cart.pricing));
                } else {
                    const copy = JSON.parse(JSON.stringify(_latest));
                    if (k === 'coord' || k === 'get-est') {
                        // console.log('force setting to', storeCheckout.checkout.cstep);
                        copy.cstep = store.checkout.cstep;
                        // copy.cstep = _oldStep;
                    }
                    store.checkout = copy;
                }

                checkout_set_gift_messages();
            });

            if (_oldStep !== store.checkout.cstep || store.uiCheckout.newCheckout) {
                store.uiCheckout.newCheckout = false;
                switch (store.checkout.cstep) {
                    case ICheckoutStep.cart:
                        runMarketingOnce('ga4_view_cart', ga4_view_cart);
                        runMarketingOnce('v4_klaviyo_atc', v4_klaviyo_atc);
                        runMarketingOnce('marketing_atc', () => marketing_atc(getItems()));
                        break;

                    case ICheckoutStep.checkout:
                        runMarketingOnce('v4_ga_checkoutStep:1', () => v4_ga_checkoutStep(1));
                        runMarketingOnce('ga4_begin_checkout', ga4_begin_checkout);
                        runMarketingOnce('v4_klaviyo_cart', v4_klaviyo_cart);
                        break;

                    case ICheckoutStep.oci:
                        runMarketingOnce('ga4_begin_checkout', ga4_begin_checkout);
                        runMarketingOnce('v4_ga_checkoutStep:2', () => v4_ga_checkoutStep(2));
                        break;

                    case ICheckoutStep.shipping_zip:
                        runMarketingOnce('v4_klaviyo_cart', v4_klaviyo_cart);
                        runMarketingOnce('v4_ga_checkoutStep:3', () => v4_ga_checkoutStep(3));
                        break;

                    case ICheckoutStep.shipping:
                    case ICheckoutStep.shipping_correction:
                        runMarketingOnce('ga4_add_shipping_info', ga4_add_shipping_info);
                        break;

                    case ICheckoutStep.shipping_methods:
                        runMarketingOnce('v4_ga_checkoutStep:3', () => v4_ga_checkoutStep(3));
                        runMarketingOnce('marketing_atc_pint', () => marketing_atc_pint(getItems(), store.checkout.email.value));
                        break;

                    case ICheckoutStep.paymentAddr:
                        runMarketingOnce('ga4_add_payment_info', ga4_add_payment_info);
                        runMarketingOnce('v4_ga_checkoutStep:4', () => v4_ga_checkoutStep(4));
                        break;

                    case ICheckoutStep.payment:
                        runMarketingOnce('ga4_add_payment_info', ga4_add_payment_info);
                        runMarketingOnce('v4_ga_checkoutStep:5', () => v4_ga_checkoutStep(5));
                        break;

                    default:
                }
            }

            if (k === 'get-cart') {
                // GA4 add_to_cart for the line the product page (or a server-side buy-now) just added.
                // Runs after the cart is loaded so the event carries the real cart line; the marker is
                // consumed on first read, so a reload cannot report the same add twice.
                pushPendingAddToCart(store.checkout.cart.items);
            }

            if (k === 'places-autocomplete-select') {
                // console.log('places-autocomplete-select done');
            }

            return true;
        })
        .catch(err => {
            logger.error(err, {key: k});
            // The request failed, so nothing on the page was updated. A page whose antiforgery token has gone stale gets a
            // 400 on every request until it is reloaded, so ask the shopper to refresh; the reload also reloads the cart.
            if (!isError && axios.isAxiosError(err)) {
                modal(CHECKOUT_REQUEST_FAILED_MODAL_ID);
            }
            return false;
        });

    function v4_ga_checkoutStep(_step) {
        try {
            const dle = {
                event: 'checkout',
                ecommerce: {
                    checkout: {
                        actionField: {step: _step, option: store.checkout.CheckoutType.toString()},
                        products: store.checkout.cart.items.map(x => {
                            const _prod: old_gaProduct = {
                                id: x.productId.toString(),
                                name: x.title2,
                                brand: 'CHC',
                                category: x.gaCategory,
                                variant: x.gaVariant,
                                coupon: x.gaDiscountCode,
                                position: x.gaPosition,
                                list: x.gaList,
                                itemId: x.itemId,
                                quantity: x.qty,
                            };
                            return _prod;
                        }),
                    },
                },
            };
            dataLayer.push(dle);
        } catch (err) {
            logger.error(err);
        }
    }

    function getItems(): ga4_item[] {
        return store.checkout.cart.items.map(x => cartLineToGa4Item(x));
    }

    function ga4_begin_checkout() {
        // add_to_cart is NOT pushed here: it fires per added line on the cart page (pushPendingAddToCart)
        // and per quantity increase in the cart controls (checkout/ga4-cart.ts).
        const rti = getRetargetItems();
        const dle: ga4_dle_begin_checkout = {
            event: 'begin_checkout',
            ecommerce: {
                currency: 'USD',
                value: store.checkout.cart.pricing.total,
                coupon: store.checkout.code_promo_text,
                items: getItems(),
            },
            retarget_items: rti,
            value: Math.round(rti.reduce((sum, item) => sum + item.value, 0)),
            ecomm_prodid: rti.map(function (item) {
                return item.id;
            }),
            ecomm_pagetype: 'basket',
            ecomm_totalvalue: Math.round(rti.reduce((sum, item) => sum + item.value, 0)),
            ecomm_inst: rti[0].inst,
        };

        clearEcommerce();
        dataLayer.push(dle);
    }

    function ga4_view_cart() {
        const dle: ga4_dle_view_cart = {
            event: 'view_cart',
            ecommerce: {
                currency: 'USD',
                value: store.checkout.cart.pricing.total,
                items: getItems(),
            },
        };
        // console.log(dle);
        clearEcommerce();
        dataLayer.push(dle);
    }

    function ga4_add_shipping_info() {
        const rti = getRetargetItems();
        const dle: ga4_dle_add_shipping_info = {
            event: 'add_shipping_info',
            ecommerce: {
                currency: 'USD',
                value: store.checkout.cart.pricing.total,
                coupon: store.checkout.code_promo_text,
                shipping_tier: store.checkout.ship.shipMethod?.desc,
                items: getItems(),
            },
            retarget_items: rti,
            value: Math.round(rti.reduce((sum, item) => sum + item.value, 0)),
            ecomm_prodid: rti.map(function (item) {
                return item.id;
            }),
            ecomm_pagetype: 'basket',
            ecomm_totalvalue: Math.round(rti.reduce((sum, item) => sum + item.value, 0)),
            ecomm_inst: rti[0].inst,
        };
        clearEcommerce();
        dataLayer.push(dle);
    }

    function ga4_add_payment_info() {
        const rti = getRetargetItems();
        const dle: ga4_dle_add_payment_info = {
            event: 'add_payment_info',
            ecommerce: {
                currency: 'USD',
                value: store.checkout.cart.pricing.total,
                coupon: store.checkout.code_promo_text,
                payment_type: store.checkout.CheckoutType === ICheckoutType.normal ? 'Credit Card' : store.checkout.CheckoutType === ICheckoutType.paypal_express ? 'PayPal Express' : store.checkout.CheckoutType === ICheckoutType.stripe_express ? 'Stripe' : 'Other',
                items: getItems(),
            },
            retarget_items: rti,
            value: Math.round(rti.reduce((sum, item) => sum + item.value, 0)),
            ecomm_prodid: rti.map(function (item) {
                return item.id;
            }),
            ecomm_pagetype: 'basket',
            ecomm_totalvalue: Math.round(rti.reduce((sum, item) => sum + item.value, 0)),
            ecomm_inst: rti[0].inst,
        };
        clearEcommerce();
        dataLayer.push(dle);
    }

    function getRetargetItems() {
        var _items = getItems();
        var prodArr: any[] = [];
        _items.map(x => {
            prodArr.push({
                id: x.item_id,
                google_business_vertical: 'retail',
                value: x.price,
                prod_type: x.item_category,
                inst: x.item_category2,
                portal: Portal?.PortalID.toString(),
            });
        });
        return prodArr;
    }

    function v4_klaviyo_atc() {
        if (store.checkout.isMyFrames || Portal?.DisableKlaviyo) {
            return;
        }

        try {
            const _klaviyoProd: any = [];
            for (let z = 0; z < store.checkout.cart.items.length; z++) {
                _klaviyoProd.push({
                    ProductID: store.checkout.cart.items[z].productId.toString(),
                    SKU: store.checkout.cart.items[z].itemId,
                    ProductName: store.checkout.cart.items[z].title2,
                    Quantity: store.checkout.cart.items[z].qty,
                    ItemPrice: store.checkout.cart.items[z].price,
                    RowTotal: store.checkout.cart.items[z].price,
                    ProductURL: store.checkout.cart.items[z].URL,
                    ImageURL: store.checkout.cart.items[z].imgFull == undefined || store.checkout.cart.items[z].imgFull.length < 1 ? `${Portal?.IMGDomain}/i/${store.checkout.cart.items[z].productId.toString()}_1280_1280.png` : `${store.checkout.cart.items[z].imgFull}`,
                    ProductCategories: [Portal && Portal.PortalID < 1 ? 'Diploma Frames' : Portal?.PortalID === 1 ? 'Custom Frames' : 'Military Frames'],
                });
            }

            if (_klaviyoProd.length > 0) {
                klaviyo.push([
                    'track',
                    'Added to Cart',
                    {
                        $value: _klaviyoProd[_klaviyoProd.length - 1].ItemPrice * _klaviyoProd[_klaviyoProd.length - 1].Quantity,
                        AddedItemProductName: _klaviyoProd[_klaviyoProd.length - 1].ProductName,
                        AddedItemProductID: _klaviyoProd[_klaviyoProd.length - 1].ProductID.toString(),
                        AddedItemSKU: _klaviyoProd[_klaviyoProd.length - 1].SKU,
                        AddedItemCategories: [Portal && Portal.PortalID < 1 ? 'Diploma Frames' : Portal?.PortalID === 1 ? 'Custom Frames' : 'Military Frames'],
                        AddedItemImageURL: _klaviyoProd[_klaviyoProd.length - 1].ImageURL.replace('.com//', '.com/'),
                        AddedItemURL: _klaviyoProd[_klaviyoProd.length - 1].URL,
                        AddedItemPrice: _klaviyoProd[_klaviyoProd.length - 1].ItemPrice,
                        AddedItemQuantity: _klaviyoProd[_klaviyoProd.length - 1].Quantity,
                        ItemNames: _klaviyoProd.map(x => x.ProductName),
                        CheckoutURL: `${__domain_name}/cart`,
                        Items: _klaviyoProd,
                    },
                ]);
            }
        } catch (err) {
            logger.error(err);
        }
    }

    function v4_klaviyo_cart() {
        if (store.checkout.isMyFrames || Portal?.DisableKlaviyo) {
            return;
        }

        try {
            const _klaviyoProd: any = [];
            for (let z = 0; z < store.checkout.cart.items.length; z++) {
                _klaviyoProd.push({
                    ProductID: store.checkout.cart.items[z].productId.toString(),
                    SKU: store.checkout.cart.items[z].itemId,
                    ProductName: store.checkout.cart.items[z].title2,
                    Quantity: store.checkout.cart.items[z].qty,
                    ItemPrice: store.checkout.cart.items[z].price,
                    RowTotal: store.checkout.cart.items[z].price,
                    ProductURL: store.checkout.cart.items[z].URL,
                    ImageURL: store.checkout.cart.items[z].imgFull == undefined || store.checkout.cart.items[z].imgFull.length < 1 ? `${Portal?.IMGDomain}/i/${store.checkout.cart.items[z].productId.toString()}_1280_1280.png` : `${store.checkout.cart.items[z].imgFull}`,
                    ProductCategories: [Portal && Portal.PortalID < 1 ? 'Diploma Frames' : Portal?.PortalID === 1 ? 'Custom Frames' : 'Military Frames'],
                });
            }

            klaviyo.push([
                'track',
                'Started Checkout',
                {
                    $event_id: `${store.checkout.cart.cartId.toString()}_${Math.round(new Date().getTime() / 1000)}`,
                    $value: store.checkout.cart.pricing.total,
                    ItemNames: _klaviyoProd.map(x => x.ProductName),
                    CheckoutURL: `${__domain_name}/cart`,
                    Items: _klaviyoProd,
                },
            ]);
        } catch (err) {
            logger.error(err);
        }
    }
};

function checkout_set_gift_messages() {
    store.checkout.giftMessages = [
        {
            GiftMessageID: 1,
            GiftMessage: 'Congratulations and Best Wishes on Your Graduation!',
            Name: 'Best Wishes on Your Graduation',
        },
        {
            GiftMessageID: 2,
            GiftMessage: 'Congratulations on your success - may the future bring even more!',
            Name: 'Congratulations',
        },
        {
            GiftMessageID: 3,
            GiftMessage: 'Wishing You A Very Merry Christmas!',
            Name: 'Merry Christmas',
        },
        {
            GiftMessageID: 4,
            GiftMessage: 'During the Festival of Lights - May happiness and love brighten your home. Best Wishes at Hanukkah!',
            Name: 'Happy Hanukkah',
        },
        {
            GiftMessageID: 5,
            GiftMessage: 'Best Wishes for a Very Happy Birthday!',
            Name: 'Happy Birthday',
        },
        {
            GiftMessageID: 6,
            GiftMessage: 'Happy Memories...Happy Moments...Happy Anniversary!',
            Name: 'Happy Anniversary',
        },
        {
            GiftMessageID: 7,
            GiftMessage: '',
            Name: ' Write my own message',
        },
    ];
}

export async function klaviyo_identify() {
    try {
        if (!store.checkout.isMyFrames) {
            klaviyo.push([
                'identify',
                {
                    $email: store.checkout.email.value,
                    $first_name: store.checkout.billing.addr.firstName.value,
                    $last_name: store.checkout.billing.addr.lastName.value,
                    $lastPortal: Portal?.PortalID,
                },
            ]);

            // dataLayer.push({event: 'user_data_submission',
            //     user_data: {
            //         email: await sha256(store.checkout.email.value),
            //         phone_number: await sha256(store.checkout.phone.value),
            //     }});
        }
    } catch (err) {
        logger.error(err);
    }
}

async function sha256(str) {
    const encoder = new TextEncoder();
    const data = encoder.encode(str.trim().toLowerCase());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}

export const statevent = async (s: IStatEvent): Promise<void> => {
    // axios.post('/api/info/event', s);
};

interface IAPIContainer {
    route: string;
    key: string;
    auth: string;
    id: number;
    msg: string;
    payload: string;
    ip: string;
    numbers_int: number[];
    numbers_dec: number[];
    booleans: boolean[];
    debug: string[];
}

interface IStatEvent {
    d1: string;
    d2: string;
    d3: string;
}
